import { FC, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router';
import cls from 'clsx';
import { Globe } from '@linktivity/link-icons';
import { Dropdown } from '@linktivity/link-ui';
import { LOCALE_OPTIONS } from '@link/share/constants';
import { useHeaderContext } from './context';
import { LocaleOption } from './header.types';
import styles from './header.module.css';

interface LocaleProps {
  localeList?: readonly LocaleOption[];
}

const Locale: FC<LocaleProps> = ({ localeList = LOCALE_OPTIONS }) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [openMenu, setOpenMenu] = useState(false);
  const { closeMobileMenu } = useHeaderContext();
  const currentLang = i18n.language;
  const currentLocaleLabel = useMemo(
    () => localeList.find(locale => locale.value === currentLang)?.label,
    [currentLang, localeList]
  );

  const changeLocale = useCallback(
    (locale: string) => {
      const searchParams = window.location.search;
      const newPath = location.pathname.replace(
        `/${currentLang}`,
        `/${locale}`
      );
      navigate(`${newPath}${searchParams}`);
      setOpenMenu(false);
      closeMobileMenu?.();
    },
    [currentLang, location.pathname, navigate, closeMobileMenu]
  );

  const localeButton = useMemo(
    () => (
      <button
        className={styles.navButton}
        type="button"
        onClick={() => setOpenMenu(visible => !visible)}
      >
        <Globe className={styles.navIcon} />
        <span className={styles.text}>{currentLocaleLabel}</span>
      </button>
    ),
    [currentLocaleLabel]
  );
  return (
    <Dropdown
      target={localeButton}
      visible={openMenu}
      sameWidth={false}
      onClose={() => setOpenMenu(false)}
    >
      <Dropdown.Menu className={styles.dropdownMenu}>
        {localeList.map(locale => (
          <Dropdown.Item key={locale.value}>
            <button
              className={cls(styles.dropdownButton, {
                [styles.active]: locale.value === currentLang,
                [styles.disabled]: !!locale.disabled
              })}
              type="button"
              disabled={!!locale.disabled}
              aria-disabled={!!locale.disabled}
              onClick={() => !locale.disabled && changeLocale(locale.value)}
            >
              {locale.label}
            </button>
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default Locale;
